import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { Box } from '../Box/index.js';
import { Flex } from '../Flex/index.js';

const StyledSwitch = styled(Box).withConfig({
  shouldForwardProp: prop => {
    return !['isChecked'].includes(prop);
  }
}).withConfig({
  displayName: "Switch__StyledSwitch",
  componentId: "sc-138mko3-0"
})(["background-color:", ";border:none;border-radius:16px;cursor:pointer;outline:none;width:48px;height:24px;position:relative;z-index:10;transition:", ";transition-property:box-shadow,background-color;", " ", " ", " ", " ", ""], _ref => {
  let {
    isChecked
  } = _ref;
  return isChecked ? themeGet('colors.selectors.default.onDefault') : themeGet('colors.selectors.default.offDefault');
}, themeGet('transitions.normal'), _ref2 => {
  let {
    isDisabled
  } = _ref2;
  return !isDisabled && css(["&:hover{background-color:", ";}"], themeGet('colors.selectors.default.offHover'));
}, _ref3 => {
  let {
    isDisabled
  } = _ref3;
  return isDisabled && css(["background-color:", ";cursor:not-allowed;"], themeGet('colors.selectors.default.offDisabled'));
}, _ref4 => {
  let {
    isChecked
  } = _ref4;
  return isChecked && css(["background-color:", ";"], themeGet('colors.selectors.default.onDefault'));
}, _ref5 => {
  let {
    isChecked,
    isDisabled
  } = _ref5;
  return isChecked && !isDisabled && css(["&:hover{background-color:", ";}"], themeGet('colors.selectors.default.onHover'));
}, _ref6 => {
  let {
    isChecked,
    isDisabled
  } = _ref6;
  return isChecked && isDisabled && css(["background-color:", ";"], themeGet('colors.selectors.default.onDisabled'));
});
const StyledToggle = styled(Box).withConfig({
  displayName: "Switch__StyledToggle",
  componentId: "sc-138mko3-1"
})(["border-radius:50%;background:", ";position:absolute;top:50%;z-index:20;transform:translateY(-50%);left:", ";transition:", ";transition-property:left;box-shadow:", ";", ":disabled &,", ":disabled:not(:checked) &{background:", ";box-shadow:none;}"], themeGet('colors.white'), _ref7 => {
  let {
    checked
  } = _ref7;
  return !checked ? '4px' : 'calc(100% - 20px)';
}, themeGet('transitions.normal'), themeGet('shadows.medium'), StyledSwitch, StyledSwitch, themeGet('colors.white'));
/**
 * Switch is used as an alternative for the Checkbox component to toggle
 * between a checked or unchecked state.
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/switch#usage)
 * - [Props](https://blockparty.endpoint.com/components/switch#props)
 * <br />
 *
 */
const Switch = _ref8 => {
  let {
    isChecked = false,
    isDisabled,
    onChange,
    dataTestId = 'switch',
    ...rest
  } = _ref8;
  useEffect(() => {
    setChecked(isChecked);
  }, [isChecked]);
  const [checked, setChecked] = useState(isChecked);
  return /*#__PURE__*/React__default.createElement(Flex, {
    display: "inline-flex"
  }, /*#__PURE__*/React__default.createElement(StyledSwitch, _extends({
    _focusVisible: {
      boxShadow: 'focus'
    },
    "aria-checked": checked,
    as: "button",
    "data-test-id": dataTestId,
    disabled: isDisabled,
    isChecked: checked,
    isDisabled: isDisabled,
    role: "switch",
    type: "button",
    onClick: e => {
      setChecked(!checked);
      onChange && onChange(e);
    }
  }, rest), /*#__PURE__*/React__default.createElement(VisuallyHidden, {
    "data-test-id": `${dataTestId}-hidden-helper-text`
  }, checked ? 'on' : 'off'), /*#__PURE__*/React__default.createElement(StyledToggle, {
    checked: checked,
    size: 16
  })));
};

export { Switch };
